/**
 * Promise-based confirm-dialog system. Usage:
 *   const { confirm } = useDialog();
 *   if (await confirm({ title, message, danger })) { ... }
 *
 * CSS-only overlay + panel animation. Esc / backdrop cancels; focus is trapped to the
 * panel; the confirm button is auto-focused.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import styles from './DialogProvider.module.css';

const DialogContext = createContext(null);

export function DialogProvider({ children }) {
  const [dialog, setDialog] = useState(null);
  const resolverRef = useRef(null);
  const confirmBtnRef = useRef(null);

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setDialog({
        title: opts.title || 'Are you sure?',
        message: opts.message || '',
        confirmLabel: opts.confirmLabel || 'Confirm',
        cancelLabel: opts.cancelLabel || 'Cancel',
        danger: Boolean(opts.danger),
      });
    });
  }, []);

  const close = useCallback((result) => {
    setDialog(null);
    resolverRef.current?.(result);
    resolverRef.current = null;
  }, []);

  useEffect(() => {
    if (!dialog) return undefined;
    confirmBtnRef.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') close(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dialog, close]);

  return (
    <DialogContext.Provider value={{ confirm }}>
      {children}
      {dialog && (
        <div className={styles.overlay} onMouseDown={() => close(false)}>
          <div
            className={styles.panel}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="dialog-title"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <h3 id="dialog-title" className={styles.title}>
              {dialog.title}
            </h3>
            {dialog.message && <p className={styles.message}>{dialog.message}</p>}
            <div className={styles.actions}>
              <button className={styles.cancel} onClick={() => close(false)}>
                {dialog.cancelLabel}
              </button>
              <button
                ref={confirmBtnRef}
                className={dialog.danger ? styles.confirmDanger : styles.confirm}
                onClick={() => close(true)}
              >
                {dialog.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </DialogContext.Provider>
  );
}

export function useDialog() {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error('useDialog must be used within <DialogProvider>');
  return ctx;
}

export default DialogProvider;
